import React, { useState, useEffect } from 'react';
import { Card, Tag, Space, Tooltip, Button, Modal } from 'antd';
import { ExperimentOutlined, InfoCircleOutlined, EyeOutlined } from '@ant-design/icons';
import { ConfigurationPreview } from './ruling';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/apiService';

const RulingConfigIndicator = ({ 
  projectId = null, 
  sessionSummaryData = null, 
  style = {},
  size = 'default' // 'small', 'default', 'large'
}) => {
  const [effectiveConfig, setEffectiveConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const { getAuthToken } = useAuth();

  useEffect(() => {
    loadEffectiveConfiguration();
  }, [projectId]);

  const loadEffectiveConfiguration = async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      const response = await apiService.getEffectiveRulingConfiguration(token, projectId);
      
      // Extract configuration from the correct field based on API response structure
      const config = response.data || response.configuration || response;
      setEffectiveConfig(config);
    } catch (error) {
      console.warn('Failed to load effective ruling configuration:', error);
      // This is expected if the system doesn't have ruling configurations set up yet
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = () => {
    setIsPreviewVisible(true);
  };

  // If session summary data indicates ruling configuration was used
  const isUsingRulingSystem = sessionSummaryData?.ruling_configuration_used || 
                             sessionSummaryData?.new_logic || 
                             effectiveConfig;

  if (!isUsingRulingSystem && !effectiveConfig) {
    return null; // Don't show indicator if not using ruling system
  }

  const configName = sessionSummaryData?.ruling_configuration?.name || 
                     effectiveConfig?.name || 
                     'System Default';

  const configVersion = sessionSummaryData?.ruling_configuration?.version || 
                        effectiveConfig?.version || 
                        '1.0.0';

  const isProjectSpecific = Boolean(effectiveConfig?.project_id);

  if (size === 'small') {
    return (
      <Space style={style}>
        <Tag 
          color="purple" 
          icon={<ExperimentOutlined />}
          style={{ fontSize: '11px' }}
        >
          Ruling System
        </Tag>
        {effectiveConfig && (
          <Tooltip title={`Configuration: ${configName} v${configVersion}`}>
            <Button
              type="text"
              size="small"
              icon={<InfoCircleOutlined />}
              onClick={handlePreview}
              style={{ padding: '0 4px', height: 'auto' }}
            />
          </Tooltip>
        )}
      </Space>
    );
  }

  return (
    <div style={style}>
      <Card
        size="small"
        style={{
          backgroundColor: 'rgba(139, 92, 246, 0.1)',
          border: '1px solid rgba(139, 92, 246, 0.3)',
          borderRadius: '6px'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ExperimentOutlined 
            style={{ 
              color: '#8b5cf6', 
              fontSize: size === 'large' ? '18px' : '14px' 
            }} 
          />
          
          <div style={{ flex: 1 }}>
            <div style={{ 
              color: '#ffffff', 
              fontWeight: '600',
              fontSize: size === 'large' ? '14px' : '13px',
              marginBottom: 2
            }}>
              Advanced Ruling System Active
            </div>
            
            <div style={{ 
              color: '#e2e8f0', 
              fontSize: '12px',
              lineHeight: '1.3'
            }}>
              <span>Configuration: <strong>{configName}</strong></span>
              <span style={{ margin: '0 6px', color: '#94a3b8' }}>•</span>
              <span>Version: {configVersion}</span>
              {isProjectSpecific && (
                <>
                  <span style={{ margin: '0 6px', color: '#94a3b8' }}>•</span>
                  <Tag size="small" color="blue">Project-specific</Tag>
                </>
              )}
            </div>
          </div>

          {effectiveConfig && (
            <div>
              <Button
                type="text"
                size="small"
                icon={<EyeOutlined />}
                onClick={handlePreview}
                style={{ 
                  color: '#8b5cf6',
                  border: '1px solid rgba(139, 92, 246, 0.3)'
                }}
              >
                View Config
              </Button>
            </div>
          )}
        </div>

        <div style={{ 
          marginTop: 8,
          fontSize: '11px',
          color: '#94a3b8',
          lineHeight: '1.3'
        }}>
          Results generated using flexible business rules instead of fixed thresholds.
        </div>
      </Card>

      {/* Configuration Preview Modal */}
      <Modal
        title="Active Ruling Configuration"
        open={isPreviewVisible}
        onCancel={() => setIsPreviewVisible(false)}
        width={800}
        footer={null}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            Loading configuration...
          </div>
        ) : effectiveConfig ? (
          <ConfigurationPreview
            configuration={effectiveConfig}
            title="Currently Active Configuration"
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
            No configuration data available
          </div>
        )}
      </Modal>
    </div>
  );
};

export default RulingConfigIndicator;